import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GitMerge } from "lucide-react";
import { dedupApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => dedupApi.reviews("pending").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const resolve = async (id, action, cand) => {
    try {
      await dedupApi.resolve(id, action === "merge"
        ? { action: "merge", target_organisation_id: cand.organisation_id, target_location_id: cand.location_id, override_reason: "review resolved" }
        : { action: "create_new" });
      toast.success(action === "merge" ? "Merged" : "Marked as new");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <h1 className="mb-4 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Duplicate review queue</h1>
      {loading ? <div className="p-6 text-sm text-slate-400">Loading…</div> :
        items.length === 0 ? <EmptyState testid="reviews-empty" icon={GitMerge} title="No reviews pending" description="Possible duplicate matches from prospect discovery appear here." /> :
        <div className="space-y-3" data-testid="review-list">
          {items.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold text-slate-400">External FSA record {r.external_establishment_id}</p>
              <div className="mt-2 space-y-2">
                {r.candidate_matches.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-2.5">
                    <span className="text-sm text-slate-600">Candidate · {Math.round(c.confidence * 100)}%</span>
                    <button data-testid={`review-merge-${r.id}-${i}`} onClick={() => resolve(r.id, "merge", c)}
                      className="rounded-md border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100">Merge into this</button>
                  </div>
                ))}
              </div>
              <button data-testid={`review-createnew-${r.id}`} onClick={() => resolve(r.id, "create_new")}
                className="mt-2 w-full rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">Not a duplicate — keep separate</button>
            </div>
          ))}
        </div>}
    </div>
  );
}
